/*
 * MaskedField — Phase S-2 (Feb 2026)
 *
 * Drop-in component for displaying a sensitive field with an eye toggle
 * that reveals the raw value for 60 seconds via POST /admin/reveal.
 *
 * Usage:
 *   <MaskedField
 *     value={user.phone}              // the already-masked value from the API
 *     kind="user.phone"               // matches REVEAL_REGISTRY keys
 *     targetId={user.id}              // resource id
 *     label="Phone"                   // optional display label
 *     testid="masked-phone"           // optional testid
 *   />
 *
 * Behaviour:
 *   • Engineering admins: clicking the eye instantly reveals (no prompt).
 *   • support_l2 / finance: prompts for a reason + (future) step-up MFA.
 *   • support_l1: eye icon is hidden (no reveal capability).
 *   • Revealed value auto-re-masks after 60 seconds.
 *   • Every reveal is audit-logged server-side.
 */
import React, { useEffect, useState } from "react";
import { Eye, EyeOff, Loader2, Lock } from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { useScopes } from "../hooks/useScopes";
import { toast } from "sonner";


// Map kind suffix → reveal-policy category, mirroring backend _kind_to_category.
function _categoryFor(kind) {
  if (!kind) return null;
  if (kind.endsWith(".phone") || kind === "twofa.sms_phone") return "phone";
  if (kind.endsWith(".email")) return "email";
  if (kind.endsWith(".ein") || kind.endsWith(".tax_id")) return "ein";
  if (kind.endsWith(".ssn") || kind.endsWith(".ssn_last4")) return "ssn";
  if (kind.endsWith(".address_line1") || kind === "address.line1" || kind.endsWith(".address_full")) {
    return "address_line1";
  }
  return null;
}


export function MaskedField({
  value,
  kind,
  targetId,
  label,
  testid,
  className = "",
  size = "default",        // "default" | "compact"
  autoRemaskMs = 60_000,
}) {
  const { hasScope, scopeRole, isWildcard } = useScopes();
  const [revealed, setRevealed] = useState(null);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  // Backend policy mirror: support_l1 cannot reveal anything.
  // Engineering reveals silently. Others must enter a reason.
  const category = _categoryFor(kind);
  const allowedCategories = {
    support_l1:  [],
    support_l2:  ["phone", "email", "address_line1"],
    finance:     ["phone", "email", "address_line1", "ein", "ssn", "last4"],
    engineering: ["*"],
  };
  const myAllowed = allowedCategories[scopeRole] || [];
  const canReveal = isWildcard || myAllowed.includes(category);
  const isEng = isWildcard;

  // Auto re-mask after autoRemaskMs.
  useEffect(() => {
    if (!revealed) return undefined;
    const t = setTimeout(() => setRevealed(null), autoRemaskMs);
    return () => clearTimeout(t);
  }, [revealed, autoRemaskMs]);

  const doReveal = async (reasonText) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/admin/reveal/${kind}/${targetId}`, {
        reason: reasonText,
      });
      setRevealed(data.value);
      toast.success(`Revealed for ${Math.round((data.ttl_seconds || 60))}s · audit-logged.`);
      setOpen(false);
      setReason("");
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "object" ? (detail.message || JSON.stringify(detail)) : (detail || e.message);
      toast.error(`Reveal blocked: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const onEyeClick = () => {
    if (revealed) {
      setRevealed(null);
      return;
    }
    if (isEng) {
      // Engineering: silent reveal with a one-word reason (still audited).
      doReveal("engineering silent reveal");
      return;
    }
    setOpen(true);
  };

  const compact = size === "compact";
  const display = revealed !== null && revealed !== undefined ? String(revealed) : (value ?? "—");

  return (
    <span data-testid={testid}
          className={`inline-flex items-center gap-1.5 ${className}`}>
      {label && <Label className="text-stone-500 text-xs uppercase tracking-wide">{label}:</Label>}
      <span data-testid={testid ? `${testid}-value` : undefined}
            className={`${revealed ? "font-mono text-emerald-700" : "text-stone-800"} ${compact ? "text-xs" : "text-sm"}`}>
        {display}
      </span>
      {/* Eye / EyeOff toggle */}
      {canReveal ? (
        <Button data-testid={testid ? `${testid}-toggle` : undefined}
                size="sm" variant="ghost" className="h-6 w-6 p-0"
                disabled={loading}
                title={revealed ? "Hide (will also auto-mask)" : "Reveal for 60 sec · audit-logged"}
                onClick={onEyeClick}>
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                   : revealed ? <EyeOff className="h-3.5 w-3.5 text-emerald-600" />
                             : <Eye className="h-3.5 w-3.5 text-stone-500" />}
        </Button>
      ) : (
        <Lock data-testid={testid ? `${testid}-locked` : undefined}
              className="h-3.5 w-3.5 text-stone-300"
              title={`Your role (${scopeRole}) cannot reveal this field.`} />
      )}

      {/* Reason dialog (support_l2 / finance) */}
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent data-testid={testid ? `${testid}-dialog` : "reveal-dialog"} className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reveal {label || kind}</DialogTitle>
            <DialogDescription>
              Revealing this PII will be audit-logged with your reason.
              The value will auto-mask in {Math.round(autoRemaskMs / 1000)} seconds.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-wide text-stone-600">
                Reason <span className="text-rose-600">*</span>
                <span className="ml-2 text-stone-400 normal-case">(≥3 chars · stored in audit chain)</span>
              </Label>
              <Textarea data-testid={testid ? `${testid}-reason` : "reveal-reason"}
                        className="mt-1" rows={3}
                        value={reason} onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. Customer called requesting their EIN for tax docs" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button data-testid={testid ? `${testid}-submit` : "reveal-submit"}
                    onClick={() => doReveal(reason.trim())}
                    disabled={loading || reason.trim().length < 3}>
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Revealing</>
                       : <><Eye className="h-4 w-4 mr-2" /> Reveal 60s</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </span>
  );
}


export default MaskedField;
